import {
  MOCK_WIDGET_SETTINGS,
  type WidgetSettingsResponse,
} from '@teo/widget-shared';
import type { ScriptTagConfig } from '../config/parse-script-config.js';
import { getWidgetAuthHeaders, getWidgetSession } from '../session/widget-session.js';

const SETTINGS_PATH = '/api/get-modules-settings';

export class WidgetSettingsError extends Error {
  readonly status: number;

  constructor(status: number, message?: string) {
    super(message ?? `[Teo Widget] Settings request failed (${status})`);
    this.name = 'WidgetSettingsError';
    this.status = status;
  }
}

export function buildSettingsUrlBySlug(config: ScriptTagConfig): string {
  if (!config.slug) {
    throw new Error('[Teo Widget] data-slug is required');
  }

  const base = config.apiBase || window.location.origin;
  const params = new URLSearchParams({
    slug: config.slug,
    module_type: config.moduleType,
  });

  return `${base.replace(/\/$/, '')}${SETTINGS_PATH}?${params.toString()}`;
}

/** GET /api/get-modules-settings?company_id={id}&module_type=300 */
export function buildSettingsUrlByCompany(
  apiBase: string,
  companyId: number | string,
  moduleType: string,
): string {
  const base = apiBase.replace(/\/$/, '');
  const params = new URLSearchParams({
    company_id: String(companyId),
    module_type: moduleType,
  });

  return `${base}${SETTINGS_PATH}?${params.toString()}`;
}

/** @deprecated Use buildSettingsUrlByCompany after login */
export const buildSettingsUrl = buildSettingsUrlBySlug;

/**
 * Fetch widget settings — requires authenticated session (`company_id` + Bearer token).
 * Called on every page load so admin changes apply without re-login.
 */
export async function fetchWidgetSettings(
  config: ScriptTagConfig,
): Promise<WidgetSettingsResponse> {
  if (config.mock) {
    return MOCK_WIDGET_SETTINGS;
  }

  const session = getWidgetSession();
  if (!session?.companyId) {
    throw new Error(
      '[Teo Widget] Authenticated session with company_id is required to fetch settings',
    );
  }

  const url = buildSettingsUrlByCompany(
    config.apiBase || window.location.origin,
    session.companyId,
    config.moduleType,
  );

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      ...getWidgetAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new WidgetSettingsError(response.status);
  }

  return response.json() as Promise<WidgetSettingsResponse>;
}

export async function fetchModulesSettingsByCompany(
  apiBase: string,
  companyId: number | string,
  moduleType: string,
): Promise<WidgetSettingsResponse> {
  const url = buildSettingsUrlByCompany(apiBase, companyId, moduleType);
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      ...getWidgetAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new WidgetSettingsError(response.status);
  }

  return response.json() as Promise<WidgetSettingsResponse>;
}
